import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { analyticsAPI } from '../services/api';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Gamepad2, 
  DollarSign, 
  Calendar,
  Activity,
  Target,
  Clock
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [gameType, setGameType] = useState('');

  const { data: businessAnalytics, isLoading: businessLoading } = useQuery(
    'businessAnalytics',
    () => analyticsAPI.getBusinessAnalytics().then(res => res.data)
  );

  const { data: engagementAnalytics, isLoading: engagementLoading } = useQuery(
    'engagementAnalytics',
    () => analyticsAPI.getEngagementAnalytics().then(res => res.data)
  );

  const { data: gameAnalytics, isLoading: gameLoading } = useQuery(
    ['gameAnalytics', gameType, timeRange],
    () => analyticsAPI.getGameAnalytics({ gameType, timeRange }).then(res => res.data)
  );

  const COLORS = ['#5865F2', '#57F287', '#FEE75C', '#EB459E', '#ED4245'];

  if (businessLoading || engagementLoading || gameLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              📊 Analytics Dashboard
            </h1>
            <p className="text-discord-light">
              Comprehensive business insights and user engagement metrics.
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Activity className="h-8 w-8 text-discord-blurple" />
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      {businessAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-discord-blurple/20 rounded-full">
                <Users className="h-6 w-6 text-discord-blurple" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-discord-light">Total Users</p>
                <p className="text-2xl font-bold text-discord-lightest">
                  {businessAnalytics.users?.total || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-discord-green/20 rounded-full">
                <Gamepad2 className="h-6 w-6 text-discord-green" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-discord-light">Total Games</p>
                <p className="text-2xl font-bold text-discord-lightest">
                  {businessAnalytics.games?.total || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-discord-yellow/20 rounded-full">
                <TrendingUp className="h-6 w-6 text-discord-yellow" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-discord-light">Conversion Rate</p>
                <p className="text-2xl font-bold text-discord-lightest">
                  {businessAnalytics.revenue?.conversionRate || 0}%
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-discord-fuchsia/20 rounded-full">
                <Calendar className="h-6 w-6 text-discord-fuchsia" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-discord-light">New Users (30d)</p>
                <p className="text-2xl font-bold text-discord-lightest">
                  {businessAnalytics.users?.newLast30Days || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        {businessAnalytics?.dailyActiveUsers && (
          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Daily Active Users</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={businessAnalytics.dailyActiveUsers}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2C2F33" />
                <XAxis 
                  dataKey="date" 
                  stroke="#B9BBBE"
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis stroke="#B9BBBE" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2C2F33', 
                    border: '1px solid #5865F2',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="activeUsers" 
                  stroke="#5865F2" 
                  strokeWidth={2}
                  dot={{ fill: '#5865F2' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Subscription Breakdown */}
        {businessAnalytics?.users?.subscriptionBreakdown && (
          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Subscription Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={businessAnalytics.users.subscriptionBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {businessAnalytics.users.subscriptionBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2C2F33', 
                    border: '1px solid #5865F2',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Game Analytics */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-discord-lightest">Game Analytics</h2>
          <div className="flex space-x-2">
            <select
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              className="input w-auto"
            >
              <option value="">All Games</option>
              <option value="tic-tac-toe">Tic-Tac-Toe</option>
              <option value="number-guess">Number Guess</option>
              <option value="rock-paper-scissors">Rock Paper Scissors</option>
            </select>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="input w-auto"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>

        {gameAnalytics?.gameMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {gameAnalytics.gameMetrics.map((metric) => (
              <div key={metric.gameType} className="p-4 bg-discord-dark rounded-lg">
                <h3 className="text-lg font-semibold text-discord-lightest mb-2">
                  {metric.gameType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-discord-light">Games:</span>
                    <span className="text-discord-lightest">{metric.totalGames}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-discord-light">Players:</span>
                    <span className="text-discord-lightest">{metric.uniquePlayers}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-discord-light">Avg Duration:</span>
                    <span className="text-discord-lightest">{metric.avgDuration}s</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Game Performance Chart */}
        {gameAnalytics?.dailyTrends && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-discord-lightest mb-4">Daily Game Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gameAnalytics.dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2C2F33" />
                <XAxis 
                  dataKey="date" 
                  stroke="#B9BBBE"
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis stroke="#B9BBBE" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2C2F33', 
                    border: '1px solid #5865F2',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="games" fill="#5865F2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Hourly Distribution */}
        {gameAnalytics?.hourlyDistribution && (
          <div>
            <h3 className="text-lg font-semibold text-discord-lightest mb-4">Hourly Game Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gameAnalytics.hourlyDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2C2F33" />
                <XAxis 
                  dataKey="_id" 
                  stroke="#B9BBBE"
                  label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }}
                />
                <YAxis stroke="#B9BBBE" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2C2F33', 
                    border: '1px solid #5865F2',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#57F287" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Engagement Metrics */}
      {engagementAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Game Completion Rates</h2>
            <div className="space-y-3">
              {engagementAnalytics.completionRates?.map((rate) => (
                <div key={rate.gameType} className="flex items-center justify-between p-3 bg-discord-dark rounded-lg">
                  <div>
                    <span className="text-discord-lightest font-medium">
                      {rate.gameType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <div className="text-sm text-discord-light">
                      {rate.completed} completed, {rate.abandoned} abandoned
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-discord-lightest">
                      {rate.completionRate}%
                    </div>
                    <div className="w-20 bg-discord-dark rounded-full h-2">
                      <div 
                        className="bg-discord-green h-2 rounded-full" 
                        style={{ width: `${rate.completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold text-discord-lightest mb-4">Average Session Duration</h2>
            <div className="space-y-3">
              {engagementAnalytics.sessionDuration?.map((session) => (
                <div key={session._id} className="flex items-center justify-between p-3 bg-discord-dark rounded-lg">
                  <div>
                    <span className="text-discord-lightest font-medium">
                      {session._id.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-discord-lightest">
                      {Math.round(session.avgDuration)}s
                    </div>
                    <div className="text-sm text-discord-light">
                      {session.minDuration}s - {session.maxDuration}s
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
